import { lazy, Suspense } from "react";

import {
  MutationCache,
  type Query,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { toast } from "design_system_radar/sonner";
import { RouterProvider } from "react-router/dom";

import AppErrorBoundary from "./components/AppErrorBoundary.tsx";
import router from "./config/routing/browserRoutes.tsx";
import { useBackendStatus } from "./hooks/useBackendStatus.ts";
import AuthProvider from "./modules/auth/auth.provider";
import ServiceUnavailablePage from "./pages/errors/ServiceUnavailablePage.tsx";
import { isBackendUnavailableError } from "./tools/httpErrors.ts";
import { captureError } from "./tools/posthog.ts";

import "@/config/style/global.css";

const ReactQueryDevtools = lazy(() =>
  import("@tanstack/react-query-devtools").then((module) => ({
    default: module.ReactQueryDevtools,
  })),
);

function getPostHogContext(
  meta: Record<string, unknown> | undefined,
  error: unknown,
): Record<string, unknown> {
  const context = meta?.posthogContext;
  if (typeof context === "function") {
    return context(error) as Record<string, unknown>;
  }
  if (context && typeof context === "object") {
    return context as Record<string, unknown>;
  }
  return {};
}

function getErrorMessage(error: unknown): string {
  const axiosError = error as AxiosError<{ errorMessage?: string }>;
  if (axiosError?.response?.status === 401) return ""; // handled by auth interceptor
  if (isBackendUnavailableError(error)) return "";
  return (
    axiosError?.response?.data?.errorMessage ||
    axiosError?.message ||
    "Ha ocurrido un error inesperado"
  );
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query: Query<unknown, unknown, unknown>) => {
      const posthogContext = getPostHogContext(query.meta, error);
      // Per-query error callbacks passed via meta (React Query v5 pattern,
      // since onError was removed from useQuery options in v5).
      const isHandled =
        typeof query.meta?.onError === "function" &&
        query.meta.onError(error) === true;

      if (isHandled) {
        captureError(error, {
          error_source: "react_query_cache",
          handled: true,
          ...posthogContext,
        });
        return;
      }

      const message = getErrorMessage(error);
      if (message) {
        toast.error(message, {
          duration: 6000,
        });
      }
      captureError(error, {
        error_source: "react_query_cache",
        ...posthogContext,
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      const message = getErrorMessage(error);
      if (message) {
        toast.error(message, {
          duration: 6000,
        });
      }
      captureError(error, { error_source: "react_mutation_cache" });
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

function App() {
  const isBackendUnavailable = useBackendStatus(
    (state) => state.isBackendUnavailable,
  );

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          {isBackendUnavailable ? (
            <ServiceUnavailablePage />
          ) : (
            <>
              {import.meta.env.DEV && (
                <Suspense fallback={null}>
                  <ReactQueryDevtools
                    buttonPosition="bottom-left"
                    initialIsOpen={false}
                  />
                </Suspense>
              )}
              <RouterProvider router={router} useTransitions />
            </>
          )}
        </AuthProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

export default App;
